import {
  type PropsWithChildren,
  useImperativeHandle,
  type Ref,
  type RefObject,
} from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { INTERACTION_MODE } from '../../core/components/interactions-overlay/types.js';
import { reduceInteractionMode } from '../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useTriggerZoom } from '../../core/components/zoom/hooks/useTriggerZoom.js';
import { ChartType } from '../../core/types/chart-types.js';
import { useApplyZoomDelta } from '../components/overlays/hooks/useApplyZoomDelta.js';
import { useResetInteractions } from '../components/overlays/hooks/useResetInteractions.js';
import { useSetState, useZoomState } from '../hooks/store/selectors.js';
import { useTimeseriesRawData } from '../hooks/useTimeseriesRawData.js';
import { TimeseriesChartConfiguration } from '../types/timeseries-config.js';
import { TimeseriesChartRef } from '../types/timeseries-ref.js';
import { normalizeConfig } from '../utils/chart-configuration.js';
import { buildAndDownloadCsv } from '../utils/download/build-and-download-csv.js';

export interface TimeseriesChartImperativeHandlerProps {
  forwardedRef: Ref<TimeseriesChartRef>;
  chartRef: RefObject<HTMLDivElement | null>;
  config: TimeseriesChartConfiguration;
}

export const TimeseriesChartImperativeHandler = ({
  forwardedRef,
  chartRef,
  config,
  children,
}: PropsWithChildren<TimeseriesChartImperativeHandlerProps>) => {
  const { pan, zoom } = config.interactions;

  const rawDataPerVariant = useTimeseriesRawData();
  const { zoomInEnabled, zoomOutEnabled } = useZoomState();
  const setState = useSetState();

  const { width } = useGridPlotSize();
  const applyZoom = useApplyZoomDelta();
  const triggerZoom = useTriggerZoom(width, applyZoom);

  const resetInteractions = useResetInteractions();

  useImperativeHandle(forwardedRef, (): TimeseriesChartRef => ({
    element: chartRef.current,
    getConfig: () =>
      JSON.stringify({
        type: ChartType.TimeSeries,
        ...normalizeConfig(config),
      }),
    downloadData: () => {
      buildAndDownloadCsv(rawDataPerVariant);
    },
    exploreMode: () => {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    },
    zoomMode: () => {
      if (zoom) {
        setState(reduceInteractionMode(INTERACTION_MODE.Zoom));
      }
    },
    panMode: () => {
      if (pan && zoomOutEnabled) {
        setState(reduceInteractionMode(INTERACTION_MODE.Pan));
      }
    },
    zoomIn: () => {
      if (zoomInEnabled) {
        triggerZoom('zoom-in');
      }
    },
    zoomOut: () => {
      if (zoomOutEnabled) {
        triggerZoom('zoom-out');
      }
    },
    reset: () => {
      resetInteractions();
    },
  }));

  return children;
};
